import I18n from 'I18n';
import { ProductType } from 'marketplace-ui-common/types/products';
import { useMemo } from 'react';
import { Filter, useFilterConfig } from '../filters/hooks/useFilterConfig';

type SafeString = ReturnType<typeof I18n.SafeString>;

interface ListingDocumentTitleProps {
  agentTeam?: string;
  categories?: string[];
  category?: string;
  companyName?: SafeString;
  name?: SafeString;
  productType?: ProductType;
}

/**
 * Hook that formats listing page titles for SEO according to the requirements:
 *
 * Breeze Agents: {Name} - AI {Team} Agent for HubSpot | {Provider}
 * Apps: {Name} - {Category} App For HubSpot | {Provider}
 */
export function useListingDocumentTitle(
  props?: ListingDocumentTitleProps
): string | null {
  const { AGENT_TEAM, CATEGORY } = useFilterConfig({
    productTypes: ['AGENT', 'APP'],
  });

  return useMemo(() => {
    if (!props?.name || !props?.productType) {
      return null;
    }

    // Handle categories - could be array or single string
    let normalizedCategories: string[] | undefined;
    if (props.category && Array.isArray(props.category)) {
      normalizedCategories = props.category;
    }

    if (props.productType === 'AGENT') {
      return formatAgentTitle(
        props.name,
        props.agentTeam,
        props.companyName,
        AGENT_TEAM
      );
    } else if (props.productType === 'SOLUTIONS_PARTNER_PROFILE') {
      return formatProfileTitle(props.name);
    } else {
      return formatAppTitle(
        props.name,
        normalizedCategories,
        props.companyName,
        CATEGORY
      );
    }
  }, [props, AGENT_TEAM, CATEGORY]);
}

function formatAgentTitle(
  name: SafeString,
  agentTeam?: string,
  companyName?: SafeString,
  agentTeamFilter?: Filter
): string {
  const teamName = getAgentTeamName(agentTeam, agentTeamFilter);

  if (teamName && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withTeam', {
      name,
      team: teamName,
      company: companyName,
    });
  } else if (teamName && !companyName) {
    return I18n.text(
      'marketplaceStorefront.listingTitles.agent.withTeamNoCompany',
      {
        name,
        team: teamName,
      }
    );
  } else if (!teamName && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withoutTeam', {
      name,
      company: companyName,
    });
  } else {
    return I18n.text(
      'marketplaceStorefront.listingTitles.agent.withoutTeamNoCompany',
      {
        name,
      }
    );
  }
}

function formatProfileTitle(name: SafeString): string {
  return I18n.text('marketplaceStorefront.listingTitles.profile.default', {
    name,
  });
}

function formatAppTitle(
  name: SafeString,
  categories?: string[],
  companyName?: SafeString,
  categoryFilter?: Filter
): string {
  const categoryPart = formatCategoriesForTitle(categories, categoryFilter);

  if (categoryPart && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.app.withCategories', {
      name,
      categories: categoryPart,
      company: companyName,
    });
  } else if (categoryPart && !companyName) {
    return I18n.text(
      'marketplaceStorefront.listingTitles.app.withCategoriesNoCompany',
      {
        name,
        categories: categoryPart,
      }
    );
  } else if (!categoryPart && companyName) {
    return I18n.text(
      'marketplaceStorefront.listingTitles.app.withoutCategories',
      {
        name,
        company: companyName,
      }
    );
  } else {
    return I18n.text(
      'marketplaceStorefront.listingTitles.app.withoutCategoriesNoCompany',
      {
        name,
      }
    );
  }
}

function getAgentTeamName(
  agentTeam?: string,
  agentTeamFilter?: Filter
): string | undefined {
  if (!agentTeam || !agentTeamFilter?.options) {
    return undefined;
  }

  // Find the matching option from the filter config
  const teamOption = agentTeamFilter.options.find(
    (option: any) => option.value === agentTeam
  );

  return teamOption?.text;
}

function formatCategoriesForTitle(
  categories?: string[],
  categoryFilter?: Filter
): string | undefined {
  if (!categories || categories.length === 0) {
    return undefined;
  }

  // Translate category enum values using filter config
  const translatedCategories = categories
    .map((category) => {
      // Find the matching option from the filter config
      const categoryOption = categoryFilter?.options?.find(
        (option: any) => option.value === category
      );
      return categoryOption?.text; // Remove fallback to prevent enum values
    })
    .filter((text): text is string => Boolean(text)); // Type guard to ensure string[]

  if (translatedCategories.length === 0) {
    return undefined;
  }

  if (translatedCategories.length === 1) {
    return translatedCategories[0];
  }

  return I18n.formatList(translatedCategories);
}
